import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, isSameDay, addDays } from 'date-fns';
import { es } from 'date-fns/locale';
import { LogOut, Calendar as CalendarIcon, Users, DollarSign, Settings, Clock, Lock, Unlock, Trash2, Star, LayoutDashboard, Scissors, Tag, Bot, TrendingUp, UserCheck, Plus, Edit2, Search, MoreVertical, ChevronDown, Filter, Download, MessageSquare, Copy, CheckCircle, XCircle, AlertCircle, User, Bell, Menu, X, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LineChart, Line, AreaChart, Area } from 'recharts';
import { useStore, buildTimeSlots, isSlotTaken, toggleBlockedSlot, removeAppointment, updateConfig, handleImgError, addBarber, updateBarber, removeBarber, addService, updateService, removeService, addPromotion, updatePromotion, removePromotion, addClient, updateClient, removeClient } from '../lib/store';
import { logout } from '../lib/auth';
import { YearCalendar } from '../components/YearCalendar';
import type { ShopConfig, Appointment, Barber, Service, Promotion, ClientRecord } from '../lib/types';
type Tab = 'dashboard' | 'calendar' | 'staff' | 'clients' | 'services' | 'finances' | 'promotions' | 'notifications' | 'stats' | 'settings' | 'profile';
export function AdminPanel() {
  const navigate = useNavigate();
  const store = useStore();
  const {
    appointments,
    blocked,
    config,
    barbers,
    services,
    promotions,
    clients
  } = store;
  const [activeTab, setActiveTab] = useState<Tab>('dashboard');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openModal, setOpenModal] = useState<'client' | 'service' | 'payment' | 'promo' | null>(null);
  const handleLogout = () => {
    logout();
    navigate('/admin');
  };
  const titleMap: Record<Tab, string> = {
    dashboard: 'Dashboard',
    calendar: 'Agenda',
    staff: 'Barberos',
    clients: 'CRM Clientes',
    services: 'Servicios',
    finances: 'Pagos',
    promotions: 'Promociones',
    notifications: 'Notificaciones',
    stats: 'Reportes',
    settings: 'Configuración',
    profile: 'Perfil Admin'
  };
  const TABS = [{
    id: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard
  }, {
    id: 'calendar',
    label: 'Agenda',
    icon: CalendarIcon
  }, {
    id: 'clients',
    label: 'Clientes',
    icon: UserCheck
  }, {
    id: 'staff',
    label: 'Barberos',
    icon: Users
  }, {
    id: 'services',
    label: 'Servicios',
    icon: Scissors
  }, {
    id: 'finances',
    label: 'Pagos',
    icon: DollarSign
  }, {
    id: 'promotions',
    label: 'Promociones',
    icon: Tag
  }, {
    id: 'notifications',
    label: 'Notificaciones',
    icon: MessageSquare
  }, {
    id: 'stats',
    label: 'Reportes',
    icon: TrendingUp
  }, {
    id: 'settings',
    label: 'Configuración',
    icon: Settings
  }] as const;
  return <div className="min-h-screen flex bg-[#0A0A0A] text-light font-sans selection:bg-gold/30" data-id="element-251">
      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setIsMobileMenuOpen(false)} data-id="element-252" />}

      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 w-64 bg-[#111111] border-r border-white/5 flex flex-col h-screen shadow-2xl z-50 transform transition-transform duration-300 ease-in-out ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`} data-id="element-253">
        <div className="p-6 flex items-center justify-between border-b border-white/5" data-id="element-254">
          <div className="flex items-center gap-3" data-id="element-255">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-gold to-yellow-600 flex items-center justify-center text-dark font-bold text-xl shadow-lg shadow-gold/20" data-id="element-256">
              B
            </div>
            <span className="font-bold tracking-widest text-lg" data-id="element-257">
              BARBER<span className="text-gold" data-id="element-258">OS</span>
            </span>
          </div>
          <button className="lg:hidden text-light-muted hover:text-light" onClick={() => setIsMobileMenuOpen(false)} data-id="element-259">
            <X size={20} data-id="element-260" />
          </button>
        </div>
        <nav className="flex-1 py-6 px-4 space-y-1 overflow-y-auto scrollbar-hide" data-id="element-261">
          <p className="px-4 text-[10px] font-semibold text-light-muted uppercase tracking-wider mb-2" data-id="element-262">
            Menú Principal
          </p>
          {TABS.map(({
          id,
          label,
          icon: Icon
        }) => <button key={id} onClick={() => {
          setActiveTab(id as Tab);
          setIsMobileMenuOpen(false);
        }} className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200 text-sm ${activeTab === id ? 'bg-gold/10 text-gold font-medium shadow-inner border border-gold/20' : 'text-light-muted hover:text-light hover:bg-white/5 border border-transparent'}`} data-id="element-263">
              <Icon size={18} className={activeTab === id ? 'text-gold' : 'text-light-muted'} data-id="element-264" />
              {label}
            </button>)}
        </nav>
        <div className="p-4 border-t border-white/5 space-y-2" data-id="element-265">
          <button onClick={() => {
          setActiveTab('profile');
          setIsMobileMenuOpen(false);
        }} className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl transition-all duration-200 text-sm ${activeTab === 'profile' ? 'bg-white/10 text-light' : 'text-light-muted hover:text-light hover:bg-white/5'}`} data-id="element-266">
            <User size={18} data-id="element-267" />
            Mi Perfil
          </button>
          <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-400/70 hover:text-red-400 hover:bg-red-500/10 rounded-xl transition-colors" data-id="element-268">
            <LogOut size={18} data-id="element-269" />
            Cerrar Sesión
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex flex-col h-screen overflow-hidden bg-[#0A0A0A] w-full min-w-0" data-id="element-270">
        <header className="bg-[#111111]/80 backdrop-blur-md border-b border-white/5 px-4 sm:px-8 py-4 sm:py-5 flex justify-between items-center shrink-0 z-10" data-id="element-271">
          <div className="flex items-center gap-3" data-id="element-272">
            <button className="lg:hidden text-light-muted hover:text-light" onClick={() => setIsMobileMenuOpen(true)} data-id="element-273">
              <Menu size={24} data-id="element-274" />
            </button>
            <div data-id="element-275">
              <h1 className="text-xl sm:text-2xl font-semibold text-light tracking-tight" data-id="element-276">
                {titleMap[activeTab]}
              </h1>
              <p className="hidden sm:block text-xs text-light-muted mt-1" data-id="element-277">
                Panel de Administración Premium
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 sm:gap-4" data-id="element-278">
            <button className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-dark-card border border-white/10 flex items-center justify-center text-light-muted hover:text-gold transition-colors relative" data-id="element-279">
              <Bell size={18} data-id="element-280" />
              <span className="absolute top-2 right-2 w-2 h-2 bg-gold rounded-full border-2 border-dark-card" data-id="element-281"></span>
            </button>
            <div className="h-8 w-px bg-white/10" data-id="element-282"></div>
            <button onClick={() => setActiveTab('profile')} className="flex items-center gap-3 hover:opacity-80 transition-opacity text-left" data-id="element-283">
              <div className="hidden sm:block" data-id="element-284">
                <p className="text-sm font-medium text-light leading-tight" data-id="element-285">
                  Admin Principal
                </p>
                <p className="text-[10px] text-gold leading-tight" data-id="element-286">
                  Propietario
                </p>
              </div>
              <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-gold to-yellow-600 rounded-full flex items-center justify-center text-dark font-bold text-sm shadow-lg shadow-gold/20 border-2 border-[#111111]" data-id="element-287">
                A
              </div>
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-4 sm:p-8 scrollbar-hide" data-id="element-288">
          <div className="max-w-7xl mx-auto" data-id="element-289">
            {activeTab === 'dashboard' && <Dashboard store={store} setOpenModal={setOpenModal} data-id="element-290" />}
            {activeTab === 'calendar' && <Agenda store={store} data-id="element-291" />}
            {activeTab === 'clients' && <Clients store={store} setOpenModal={setOpenModal} data-id="element-292" />}
            {activeTab === 'staff' && <Staff store={store} data-id="element-293" />}
            {activeTab === 'services' && <ServicesList store={store} setOpenModal={setOpenModal} data-id="element-294" />}
            {activeTab === 'finances' && <Finances store={store} setOpenModal={setOpenModal} data-id="element-295" />}
            {activeTab === 'promotions' && <PromotionsList store={store} setOpenModal={setOpenModal} data-id="element-296" />}
            {activeTab === 'notifications' && <Notifications store={store} data-id="element-297" />}
            {activeTab === 'stats' && <Stats store={store} data-id="element-298" />}
            {activeTab === 'settings' && <SettingsForm store={store} data-id="element-299" />}
            {activeTab === 'profile' && <AdminProfile data-id="element-300" />}
          </div>
        </div>
      </main>

      {/* Modals */}
      {openModal === 'client' && <ClientModal store={store} onClose={() => setOpenModal(null)} data-id="element-301" />}
      {openModal === 'service' && <ServiceModal store={store} onClose={() => setOpenModal(null)} data-id="element-302" />}
      {openModal === 'payment' && <PaymentModal store={store} onClose={() => setOpenModal(null)} data-id="element-303" />}
      {openModal === 'promo' && <PromoModal store={store} onClose={() => setOpenModal(null)} data-id="element-304" />}
    </div>;
}
// --- SECTIONS ---
function Dashboard({
  store,
  setOpenModal
}: {
  store: any;
  setOpenModal: any;
}) {
  const {
    appointments,
    barbers,
    services,
    clients
  } = store;
  const today = new Date().toISOString().slice(0, 10);
  const [filter, setFilter] = useState<'day' | 'week' | 'month'>('week');
  const todayAppts = appointments.filter((a: any) => a.date === today && a.status !== 'cancelled');
  const todayIncome = todayAppts.filter((a: any) => a.paymentStatus === 'approved').reduce((sum: number, a: any) => sum + a.price, 0);
  const monthStart = new Date().toISOString().slice(0, 7);
  const monthAppts = appointments.filter((a: any) => a.date.startsWith(monthStart) && a.status !== 'cancelled');
  const monthIncome = monthAppts.filter((a: any) => a.paymentStatus === 'approved').reduce((sum: number, a: any) => sum + a.price, 0);
  const newClients = clients.filter((c: any) => c.status === 'Nuevo').length;
  const recurringClients = clients.filter((c: any) => c.status === 'Frecuente' || c.status === 'VIP').length;
  const cancellations = appointments.filter((a: any) => a.status === 'cancelled').length;
  const upcoming = appointments.filter((a: any) => a.status !== 'cancelled' && a.date >= today).sort((a: any, b: any) => (a.date + a.time).localeCompare(b.date + b.time))[0];
  // Top Barber
  const barberCounts = appointments.reduce((acc: any, a: any) => {
    if (a.status !== 'cancelled') acc[a.barberName] = (acc[a.barberName] || 0) + 1;
    return acc;
  }, {});
  const topBarber = Object.entries(barberCounts).sort((a: any, b: any) => b[1] - a[1])[0]?.[0] || 'N/A';
  // Top Service
  const serviceCounts = appointments.reduce((acc: any, a: any) => {
    if (a.status !== 'cancelled') acc[a.serviceName] = (acc[a.serviceName] || 0) + 1;
    return acc;
  }, {});
  const topService = Object.entries(serviceCounts).sort((a: any, b: any) => b[1] - a[1])[0]?.[0] || 'N/A';
  // Chart Data
  const chartData = useMemo(() => {
    const days = filter === 'week' ? 7 : filter === 'month' ? 30 : 1;
    return Array.from({
      length: days
    }).map((_, i) => {
      const d = subDays(new Date(), days - 1 - i);
      const dateStr = d.toISOString().slice(0, 10);
      const dayAppts = appointments.filter((a: any) => a.date === dateStr && a.status !== 'cancelled');
      const income = dayAppts.filter((a: any) => a.paymentStatus === 'approved').reduce((sum: number, a: any) => sum + a.price, 0);
      return {
        name: format(d, filter === 'month' ? 'dd/MM' : 'EEE', {
          locale: es
        }),
        reservas: dayAppts.length,
        ingresos: income
      };
    });
  }, [appointments, filter]);
  return <div className="space-y-8" data-id="element-305">
      {/* Quick Actions */}
      <div className="flex flex-wrap gap-3" data-id="element-306">
        <button className="bg-gold text-dark px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-gold-hover transition-colors flex items-center gap-2 shadow-lg shadow-gold/20" data-id="element-307">
          <Plus size={16} data-id="element-308" /> Nuevo turno
        </button>
        <button onClick={() => setOpenModal('client')} className="bg-dark-card border border-white/10 text-light px-5 py-2.5 rounded-xl text-sm font-medium hover:bg-white/5 transition-colors flex items-center gap-2" data-id="element-309">
          <UserPlus size={16} data-id="element-310" /> Nuevo cliente
        </button>
        <button onClick={() => setOpenModal('service')} className="bg-dark-card border border-white/10 text-light px-5 py-2.5 rounded-xl text-sm font-medium hover:bg-white/5 transition-colors flex items-center gap-2" data-id="element-311">
          <Scissors size={16} data-id="element-312" /> Nuevo servicio
        </button>
        <button onClick={() => setOpenModal('payment')} className="bg-dark-card border border-white/10 text-light px-5 py-2.5 rounded-xl text-sm font-medium hover:bg-white/5 transition-colors flex items-center gap-2" data-id="element-313">
          <DollarSign size={16} data-id="element-314" /> Registrar pago
        </button>
        <button onClick={() => setOpenModal('promo')} className="bg-dark-card border border-white/10 text-light px-5 py-2.5 rounded-xl text-sm font-medium hover:bg-white/5 transition-colors flex items-center gap-2" data-id="element-315">
          <Tag size={16} data-id="element-316" /> Crear promoción
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4" data-id="element-317">
        <KpiCard title="Ingresos del día" value={`$${todayIncome.toLocaleString('es-UY')}`} icon={<DollarSign className="text-green-400" data-id="element-319" />} trend="+12%" data-id="element-318" />
        <KpiCard title="Ingresos del mes" value={`$${monthIncome.toLocaleString('es-UY')}`} icon={<TrendingUp className="text-gold" data-id="element-321" />} data-id="element-320" />
        <KpiCard title="Turnos de hoy" value={todayAppts.length} icon={<CalendarIcon className="text-blue-400" data-id="element-323" />} data-id="element-322" />
        <KpiCard title="Próximo turno" value={upcoming ? `${upcoming.time}hs` : 'Ninguno'} subtitle={upcoming?.clientName} icon={<Clock className="text-purple-400" data-id="element-325" />} data-id="element-324" />
        <KpiCard title="Clientes nuevos" value={newClients} icon={<UserPlus className="text-pink-400" data-id="element-327" />} data-id="element-326" />
        <KpiCard title="Clientes recurrentes" value={recurringClients} icon={<Users className="text-indigo-400" data-id="element-329" />} data-id="element-328" />
        <KpiCard title="Cancelaciones" value={cancellations} icon={<XCircle className="text-red-400" data-id="element-331" />} data-id="element-330" />
        <KpiCard title="Barbero Top" value={topBarber} icon={<Star className="text-yellow-400" data-id="element-333" />} data-id="element-332" />
        <KpiCard title="Servicio Top" value={topService} icon={<Scissors className="text-orange-400" data-id="element-335" />} data-id="element-334" />
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-6" data-id="element-336">
        <div className="bg-[#111111] rounded-2xl border border-white/5 p-6 shadow-xl" data-id="element-337">
          <div className="flex justify-between items-center mb-6" data-id="element-338">
            <h3 className="text-base font-semibold text-light" data-id="element-339">
              Evolución de Ingresos
            </h3>
            <select value={filter} onChange={e => setFilter(e.target.value as any)} className="bg-dark border border-white/10 rounded-lg px-3 py-1.5 text-xs text-light focus:outline-none focus:border-gold" data-id="element-340">
              <option value="week" data-id="element-341">Últimos 7 días</option>
              <option value="month" data-id="element-342">Últimos 30 días</option>
            </select>
          </div>
          <div className="h-72" data-id="element-343">
            <ResponsiveContainer width="100%" height="100%" data-id="element-344">
              <AreaChart data={chartData} data-id="element-345">
                <defs data-id="element-346">
                  <linearGradient id="colorIngresos" x1="0" y1="0" x2="0" y2="1" data-id="element-347">
                    <stop offset="5%" stopColor="#C8A548" stopOpacity={0.3} data-id="element-348" />
                    <stop offset="95%" stopColor="#C8A548" stopOpacity={0} data-id="element-349" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#222" vertical={false} data-id="element-350" />
                <XAxis dataKey="name" stroke="#666" fontSize={12} tickLine={false} axisLine={false} data-id="element-351" />
                <YAxis stroke="#666" fontSize={12} tickLine={false} axisLine={false} tickFormatter={v => `$${v}`} data-id="element-352" />
                <Tooltip contentStyle={{
                backgroundColor: '#1A1A1A',
                borderColor: '#333',
                borderRadius: '8px'
              }} itemStyle={{
                color: '#C8A548'
              }} data-id="element-353" />
                <Area type="monotone" dataKey="ingresos" stroke="#C8A548" strokeWidth={3} fillOpacity={1} fill="url(#colorIngresos)" data-id="element-354" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-[#111111] rounded-2xl border border-white/5 p-6 shadow-xl" data-id="element-355">
          <h3 className="text-base font-semibold text-light mb-6" data-id="element-356">
            Volumen de Reservas
          </h3>
          <div className="h-72" data-id="element-357">
            <ResponsiveContainer width="100%" height="100%" data-id="element-358">
              <BarChart data={chartData} data-id="element-359">
                <CartesianGrid strokeDasharray="3 3" stroke="#222" vertical={false} data-id="element-360" />
                <XAxis dataKey="name" stroke="#666" fontSize={12} tickLine={false} axisLine={false} data-id="element-361" />
                <YAxis stroke="#666" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} data-id="element-362" />
                <Tooltip cursor={{
                fill: '#222'
              }} contentStyle={{
                backgroundColor: '#1A1A1A',
                borderColor: '#333',
                borderRadius: '8px'
              }} data-id="element-363" />
                <Bar dataKey="reservas" fill="#4A90D9" radius={[4, 4, 0, 0]} maxBarSize={40} data-id="element-364" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>;
}
function KpiCard({
  title,
  value,
  subtitle,
  icon,
  trend
}: any) {
  return <div className="bg-[#111111] rounded-2xl border border-white/5 p-5 shadow-lg hover:border-white/10 transition-colors group" data-id="element-365">
      <div className="flex justify-between items-start mb-2" data-id="element-366">
        <p className="text-xs font-medium text-light-muted group-hover:text-light transition-colors" data-id="element-367">
          {title}
        </p>
        <div className="p-2 bg-dark rounded-lg border border-white/5" data-id="element-368">
          {icon}
        </div>
      </div>
      <div className="flex items-end gap-3" data-id="element-369">
        <h4 className="text-2xl font-bold text-light tracking-tight" data-id="element-370">
          {value}
        </h4>
        {trend && <span className="text-xs font-medium text-green-400 mb-1" data-id="element-371">
            {trend}
          </span>}
      </div>
      {subtitle && <p className="text-[10px] text-light-muted mt-1 truncate" data-id="element-372">{subtitle}</p>}
    </div>;
}
function UserPlus(props: any) {
  return <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props} data-id="element-373">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" data-id="element-374" />
      <circle cx="9" cy="7" r="4" data-id="element-375" />
      <line x1="19" y1="8" x2="19" y2="14" data-id="element-376" />
      <line x1="22" y1="11" x2="16" y2="11" data-id="element-377" />
    </svg>;
}
function Agenda({
  store
}: {
  store: any;
}) {
  const {
    appointments,
    blocked,
    config,
    barbers
  } = store;
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [view, setView] = useState<'day' | 'week' | 'month'>('day');
  const [barberFilter, setBarberFilter] = useState<string>('all');
  const dateKey = format(selectedDate, 'yyyy-MM-dd');
  const timeSlots = buildTimeSlots(config);
  const filteredAppts = appointments.filter((a: any) => a.date === dateKey && (barberFilter === 'all' || a.barberId === barberFilter));
  return <div className="grid xl:grid-cols-[320px_1fr] gap-8 items-start" data-id="element-378">
      <div className="space-y-6" data-id="element-379">
        <YearCalendar selectedDate={selectedDate} onSelect={setSelectedDate} disablePast={false} data-id="element-380" />

        <div className="bg-[#111111] rounded-2xl border border-white/5 p-5 shadow-lg" data-id="element-381">
          <h3 className="text-sm font-semibold text-light mb-4" data-id="element-382">Filtros</h3>
          <div className="space-y-4" data-id="element-383">
            <div data-id="element-384">
              <label className="text-xs text-light-muted block mb-2" data-id="element-385">
                Vista
              </label>
              <div className="flex bg-dark rounded-lg p-1 border border-white/5" data-id="element-386">
                {['day', 'week', 'month'].map(v => <button key={v} onClick={() => setView(v as any)} className={`flex-1 text-xs py-1.5 rounded-md capitalize transition-colors ${view === v ? 'bg-white/10 text-light font-medium' : 'text-light-muted hover:text-light'}`} data-id="element-387">
                    {v === 'day' ? 'Día' : v === 'week' ? 'Semana' : 'Mes'}
                  </button>)}
              </div>
            </div>
            <div data-id="element-388">
              <label className="text-xs text-light-muted block mb-2" data-id="element-389">
                Barbero
              </label>
              <select value={barberFilter} onChange={e => setBarberFilter(e.target.value)} className="w-full bg-dark border border-white/10 rounded-xl px-3 py-2.5 text-sm text-light focus:outline-none focus:border-gold" data-id="element-390">
                <option value="all" data-id="element-391">Todos los barberos</option>
                {barbers.map((b: any) => <option key={b.id} value={b.id} data-id="element-392">
                    {b.name}
                  </option>)}
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-[#111111] rounded-2xl border border-white/5 p-6 shadow-xl min-h-[600px] flex flex-col" data-id="element-393">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 pb-4 border-b border-white/5" data-id="element-394">
          <div data-id="element-395">
            <h2 className="text-xl font-bold text-light capitalize" data-id="element-396">
              {format(selectedDate, "EEEE d 'de' MMMM, yyyy", {
              locale: es
            })}
            </h2>
            <p className="text-sm text-light-muted mt-1" data-id="element-397">
              {filteredAppts.length} turnos programados
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto" data-id="element-398">
            <button className="w-full sm:w-auto justify-center bg-dark-card border border-white/10 text-light px-4 py-2 rounded-xl text-sm font-medium hover:bg-white/5 transition-colors flex items-center gap-2" data-id="element-399">
              <CalendarIcon size={16} data-id="element-400" /> Marcar Vacaciones
            </button>
            <button className="w-full sm:w-auto justify-center bg-gold text-dark px-4 py-2 rounded-xl text-sm font-semibold hover:bg-gold-hover transition-colors flex items-center gap-2 shadow-lg shadow-gold/20" data-id="element-401">
              <Plus size={16} data-id="element-402" /> Turno Manual
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto pr-2 space-y-3 scrollbar-hide" data-id="element-403">
          {timeSlots.map((time: string) => {
          const apptsAtTime = filteredAppts.filter((a: any) => a.time === time && a.status !== 'cancelled');
          const isBlocked = blocked.some((b: any) => b.date === dateKey && b.time === time && (barberFilter === 'all' || b.barberId === barberFilter));
          return <div key={time} className="flex gap-4 group" data-id="element-404">
                <div className="w-16 text-right pt-3 shrink-0" data-id="element-405">
                  <span className="text-sm font-medium text-light-muted" data-id="element-406">
                    {time}
                  </span>
                </div>
                <div className="flex-1 relative min-h-[60px] bg-dark rounded-xl border border-white/5 p-2 group-hover:border-white/10 transition-colors flex flex-col gap-2" data-id="element-407">
                  {apptsAtTime.length === 0 && !isBlocked && <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-408">
                      <button className="text-xs text-gold font-medium flex items-center gap-1 bg-gold/10 px-3 py-1.5 rounded-lg" data-id="element-409">
                        <Plus size={12} data-id="element-410" /> Agregar turno
                      </button>
                    </div>}

                  {isBlocked && <div className="w-full h-full bg-red-500/5 border border-red-500/20 rounded-lg flex items-center justify-center text-red-400/70 text-xs" data-id="element-411">
                      <Lock size={14} className="mr-2" data-id="element-412" /> Horario Bloqueado
                    </div>}

                  {apptsAtTime.map((appt: any) => <div key={appt.id} className="bg-[#1A1A1A] border border-white/10 rounded-lg p-3 flex justify-between items-center shadow-sm hover:border-gold/30 transition-colors" data-id="element-413">
                      <div className="flex items-center gap-4" data-id="element-414">
                        <img src={appt.barberImage} alt="" onError={handleImgError} className="w-10 h-10 rounded-full object-cover object-[center_20%] border border-white/10" data-id="element-415" />
                        <div data-id="element-416">
                          <p className="font-semibold text-light text-sm" data-id="element-417">
                            {appt.clientName}
                          </p>
                          <p className="text-xs text-light-muted" data-id="element-418">
                            {appt.serviceName} • {appt.barberName}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-6" data-id="element-419">
                        <div className="text-right" data-id="element-420">
                          <p className="font-semibold text-gold text-sm" data-id="element-421">
                            ${appt.price.toLocaleString('es-UY')}
                          </p>
                          <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium mt-1 ${appt.paymentStatus === 'approved' ? 'bg-green-500/10 text-green-400 border border-green-500/20' : 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20'}`} data-id="element-422">
                            {appt.paymentStatus === 'approved' ? 'Pagado' : 'Pendiente'}
                          </span>
                        </div>
                        <div className="flex gap-1" data-id="element-423">
                          <button className="p-1.5 text-light-muted hover:text-light bg-dark rounded-md border border-white/5 hover:border-white/20 transition-colors" title="Editar" data-id="element-424">
                            <Edit2 size={14} data-id="element-425" />
                          </button>
                          <button className="p-1.5 text-light-muted hover:text-red-400 bg-dark rounded-md border border-white/5 hover:border-red-500/30 transition-colors" title="Cancelar" data-id="element-426">
                            <XCircle size={14} data-id="element-427" />
                          </button>
                        </div>
                      </div>
                    </div>)}
                </div>
              </div>;
        })}
        </div>
      </div>
    </div>;
}
function Clients({
  store,
  setOpenModal
}: {
  store: any;
  setOpenModal: any;
}) {
  const {
    clients
  } = store;
  const [search, setSearch] = useState('');
  const filtered = clients.filter((c: any) => c.name.toLowerCase().includes(search.toLowerCase()) || c.phone.includes(search) || c.email.toLowerCase().includes(search.toLowerCase()));
  return <div className="space-y-6" data-id="element-428">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-429">
        <div className="relative w-full sm:w-96" data-id="element-430">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-light-muted" size={18} data-id="element-431" />
          <input type="text" placeholder="Buscar por nombre, teléfono o email..." value={search} onChange={e => setSearch(e.target.value)} className="w-full bg-[#111111] border border-white/10 rounded-xl pl-10 pr-4 py-2.5 text-sm text-light focus:outline-none focus:border-gold shadow-inner" data-id="element-432" />
        </div>
        <button onClick={() => setOpenModal('client')} className="w-full sm:w-auto bg-gold text-dark px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-gold-hover transition-colors flex justify-center items-center gap-2 shadow-lg shadow-gold/20" data-id="element-433">
          <UserPlus size={16} data-id="element-434" /> Nuevo Cliente
        </button>
      </div>

      <div className="bg-[#111111] rounded-2xl border border-white/5 overflow-x-auto shadow-xl" data-id="element-435">
        <table className="w-full text-left text-sm min-w-[800px]" data-id="element-436">
          <thead className="bg-dark/50 text-light-muted text-xs uppercase tracking-wider border-b border-white/5" data-id="element-437">
            <tr data-id="element-438">
              <th className="px-6 py-4 font-medium" data-id="element-439">Cliente</th>
              <th className="px-6 py-4 font-medium" data-id="element-440">Contacto</th>
              <th className="px-6 py-4 font-medium" data-id="element-441">Estado</th>
              <th className="px-6 py-4 font-medium" data-id="element-442">Visitas</th>
              <th className="px-6 py-4 font-medium" data-id="element-443">Total Gastado</th>
              <th className="px-6 py-4 font-medium text-right" data-id="element-444">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5" data-id="element-445">
            {filtered.map((c: any) => <tr key={c.id} className="hover:bg-white/5 transition-colors group" data-id="element-446">
                <td className="px-6 py-4" data-id="element-447">
                  <div className="flex items-center gap-3" data-id="element-448">
                    <div className="w-10 h-10 rounded-full bg-dark border border-white/10 flex items-center justify-center text-gold font-bold" data-id="element-449">
                      {c.name.charAt(0)}
                    </div>
                    <div data-id="element-450">
                      <p className="font-semibold text-light" data-id="element-451">{c.name}</p>
                      <p className="text-[10px] text-light-muted" data-id="element-452">
                        Última visita: {c.lastVisit || 'N/A'}
                      </p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4" data-id="element-453">
                  <p className="text-light" data-id="element-454">{c.phone}</p>
                  <p className="text-xs text-light-muted" data-id="element-455">{c.email}</p>
                </td>
                <td className="px-6 py-4" data-id="element-456">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${c.status === 'VIP' ? 'bg-purple-500/10 text-purple-400 border-purple-500/20' : c.status === 'Frecuente' ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' : 'bg-green-500/10 text-green-400 border-green-500/20'}`} data-id="element-457">
                    {c.status}
                  </span>
                </td>
                <td className="px-6 py-4 text-light" data-id="element-458">{c.visits || 0}</td>
                <td className="px-6 py-4 font-medium text-gold" data-id="element-459">
                  ${(c.spent || 0).toLocaleString('es-UY')}
                </td>
                <td className="px-6 py-4 text-right" data-id="element-460">
                  <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-461">
                    <button className="p-2 text-light-muted hover:text-green-400 bg-dark rounded-lg border border-white/5" title="WhatsApp" data-id="element-462">
                      <MessageSquare size={16} data-id="element-463" />
                    </button>
                    <button className="p-2 text-light-muted hover:text-gold bg-dark rounded-lg border border-white/5" title="Editar" data-id="element-464">
                      <Edit2 size={16} data-id="element-465" />
                    </button>
                    <button className="p-2 text-light-muted hover:text-light bg-dark rounded-lg border border-white/5" title="Ver Perfil" data-id="element-466">
                      <ChevronRight size={16} data-id="element-467" />
                    </button>
                  </div>
                </td>
              </tr>)}
            {filtered.length === 0 && <tr data-id="element-468">
                <td colSpan={6} className="px-6 py-12 text-center text-light-muted" data-id="element-469">
                  No se encontraron clientes.
                </td>
              </tr>}
          </tbody>
        </table>
      </div>
    </div>;
}
function Staff({
  store
}: {
  store: any;
}) {
  const {
    barbers
  } = store;
  return <div className="space-y-6" data-id="element-470">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-471">
        <h2 className="text-lg font-semibold text-light" data-id="element-472">Equipo de Barberos</h2>
        <button className="w-full sm:w-auto bg-gold text-dark px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-gold-hover transition-colors flex justify-center items-center gap-2 shadow-lg shadow-gold/20" data-id="element-473">
          <Plus size={16} data-id="element-474" /> Agregar Barbero
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6" data-id="element-475">
        {barbers.map((b: any) => <div key={b.id} className="bg-[#111111] rounded-2xl border border-white/5 p-6 shadow-xl relative group overflow-hidden" data-id="element-476">
            <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-477">
              <button className="p-2 text-light-muted hover:text-gold bg-dark rounded-lg border border-white/10" data-id="element-478">
                <Edit2 size={14} data-id="element-479" />
              </button>
              <button className="p-2 text-light-muted hover:text-red-400 bg-dark rounded-lg border border-white/10" data-id="element-480">
                <Trash2 size={14} data-id="element-481" />
              </button>
            </div>
            <div className="flex items-center gap-5 mb-6" data-id="element-482">
              <img src={b.image} alt={b.name} onError={handleImgError} className="w-20 h-20 rounded-full object-cover object-[center_20%] border-2 border-white/10" data-id="element-483" />
              <div data-id="element-484">
                <h3 className="text-xl font-bold text-light" data-id="element-485">{b.name}</h3>
                <p className="text-sm text-gold font-medium" data-id="element-486">{b.role}</p>
                <div className="flex items-center gap-1 text-xs text-light-muted mt-1" data-id="element-487">
                  <Star size={12} className="text-gold" fill="currentColor" data-id="element-488" />{' '}
                  {b.rating} ({b.reviews})
                </div>
              </div>
            </div>
            <div className="space-y-3 text-sm" data-id="element-489">
              <div className="flex justify-between py-2 border-b border-white/5" data-id="element-490">
                <span className="text-light-muted" data-id="element-491">Estado</span>
                <span className={`font-medium ${b.status === 'Ausente' ? 'text-red-400' : 'text-green-400'}`} data-id="element-492">
                  {b.status || 'Disponible'}
                </span>
              </div>
              <div className="flex justify-between py-2 border-b border-white/5" data-id="element-493">
                <span className="text-light-muted" data-id="element-494">Horario</span>
                <span className="text-light" data-id="element-495">
                  {b.schedule || '09:00 - 20:00'}
                </span>
              </div>
              <div className="flex justify-between py-2 border-b border-white/5" data-id="element-496">
                <span className="text-light-muted" data-id="element-497">Comisión</span>
                <span className="text-light" data-id="element-498">{b.commission || 50}%</span>
              </div>
              <div className="flex justify-between py-2" data-id="element-499">
                <span className="text-light-muted" data-id="element-500">Especialidades</span>
                <span className="text-light text-right max-w-[60%] truncate" title={b.specialties.join(', ')} data-id="element-501">
                  {b.specialties.join(', ')}
                </span>
              </div>
            </div>
          </div>)}
      </div>
    </div>;
}
function ServicesList({
  store,
  setOpenModal
}: {
  store: any;
  setOpenModal: any;
}) {
  const {
    services
  } = store;
  return <div className="space-y-6" data-id="element-502">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-503">
        <h2 className="text-lg font-semibold text-light" data-id="element-504">
          Catálogo de Servicios
        </h2>
        <button onClick={() => setOpenModal('service')} className="w-full sm:w-auto bg-gold text-dark px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-gold-hover transition-colors flex justify-center items-center gap-2 shadow-lg shadow-gold/20" data-id="element-505">
          <Plus size={16} data-id="element-506" /> Crear Servicio
        </button>
      </div>
      <div className="bg-[#111111] rounded-2xl border border-white/5 overflow-x-auto shadow-xl" data-id="element-507">
        <table className="w-full text-left text-sm min-w-[800px]" data-id="element-508">
          <thead className="bg-dark/50 text-light-muted text-xs uppercase tracking-wider border-b border-white/5" data-id="element-509">
            <tr data-id="element-510">
              <th className="px-6 py-4 font-medium" data-id="element-511">Servicio</th>
              <th className="px-6 py-4 font-medium" data-id="element-512">Categoría</th>
              <th className="px-6 py-4 font-medium" data-id="element-513">Duración</th>
              <th className="px-6 py-4 font-medium" data-id="element-514">Precio</th>
              <th className="px-6 py-4 font-medium" data-id="element-515">Estado</th>
              <th className="px-6 py-4 font-medium text-right" data-id="element-516">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5" data-id="element-517">
            {services.map((s: any) => <tr key={s.id} className="hover:bg-white/5 transition-colors group" data-id="element-518">
                <td className="px-6 py-4" data-id="element-519">
                  <p className="font-semibold text-light" data-id="element-520">{s.name}</p>
                  <p className="text-[10px] text-light-muted truncate max-w-xs" data-id="element-521">
                    {s.desc}
                  </p>
                </td>
                <td className="px-6 py-4" data-id="element-522">
                  <span className="px-2.5 py-1 bg-white/5 rounded-lg text-xs text-light-muted border border-white/10" data-id="element-523">
                    {s.category}
                  </span>
                </td>
                <td className="px-6 py-4 text-light flex items-center gap-1.5 mt-2" data-id="element-524">
                  <Clock size={14} className="text-light-muted" data-id="element-525" /> {s.duration}
                </td>
                <td className="px-6 py-4 font-semibold text-gold" data-id="element-526">
                  ${s.price.toLocaleString('es-UY')}
                </td>
                <td className="px-6 py-4" data-id="element-527">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${s.active !== false ? 'bg-green-500/10 text-green-400 border-green-500/20' : 'bg-red-500/10 text-red-400 border-red-500/20'}`} data-id="element-528">
                    {s.active !== false ? 'Activo' : 'Inactivo'}
                  </span>
                </td>
                <td className="px-6 py-4 text-right" data-id="element-529">
                  <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-530">
                    <button className="p-2 text-light-muted hover:text-light bg-dark rounded-lg border border-white/5" title="Duplicar" data-id="element-531">
                      <Copy size={16} data-id="element-532" />
                    </button>
                    <button className="p-2 text-light-muted hover:text-gold bg-dark rounded-lg border border-white/5" title="Editar" data-id="element-533">
                      <Edit2 size={16} data-id="element-534" />
                    </button>
                    <button className="p-2 text-light-muted hover:text-red-400 bg-dark rounded-lg border border-white/5" title="Eliminar" data-id="element-535">
                      <Trash2 size={16} data-id="element-536" />
                    </button>
                  </div>
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
}
function Finances({
  store,
  setOpenModal
}: {
  store: any;
  setOpenModal: any;
}) {
  const {
    appointments
  } = store;
  const paid = appointments.filter((a: any) => a.paymentStatus === 'approved' && a.status !== 'cancelled');
  const total = paid.reduce((sum: number, a: any) => sum + a.price, 0);
  return <div className="space-y-6" data-id="element-537">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-538">
        <h2 className="text-lg font-semibold text-light" data-id="element-539">Control de Pagos</h2>
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto" data-id="element-540">
          <button onClick={() => setOpenModal('payment')} className="w-full sm:w-auto bg-gold text-dark px-4 py-2.5 rounded-xl text-sm font-semibold hover:bg-gold-hover transition-colors flex justify-center items-center gap-2 shadow-lg shadow-gold/20" data-id="element-541">
            <DollarSign size={16} data-id="element-542" /> Registrar Pago
          </button>
          <select className="w-full sm:w-auto bg-[#111111] border border-white/10 rounded-xl px-4 py-2.5 text-sm text-light focus:outline-none focus:border-gold" data-id="element-543">
            <option data-id="element-544">Este Mes</option>
            <option data-id="element-545">Esta Semana</option>
            <option data-id="element-546">Hoy</option>
            <option data-id="element-547">Este Año</option>
          </select>
          <button className="w-full sm:w-auto bg-dark-card border border-white/10 text-light px-4 py-2.5 rounded-xl text-sm font-medium hover:bg-white/5 transition-colors flex justify-center items-center gap-2" data-id="element-548">
            <Download size={16} data-id="element-549" /> Exportar CSV
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-550">
        <div className="bg-gradient-to-br from-gold/20 to-dark-card rounded-2xl border border-gold/20 p-6 shadow-xl" data-id="element-551">
          <p className="text-sm font-medium text-gold mb-2" data-id="element-552">
            Recaudación Total
          </p>
          <h3 className="text-4xl font-bold text-light" data-id="element-553">
            ${total.toLocaleString('es-UY')}
          </h3>
        </div>
        <div className="bg-[#111111] rounded-2xl border border-white/5 p-6 shadow-xl" data-id="element-554">
          <p className="text-sm font-medium text-light-muted mb-2" data-id="element-555">
            Transacciones Exitosas
          </p>
          <h3 className="text-4xl font-bold text-light" data-id="element-556">{paid.length}</h3>
        </div>
        <div className="bg-[#111111] rounded-2xl border border-white/5 p-6 shadow-xl" data-id="element-557">
          <p className="text-sm font-medium text-light-muted mb-2" data-id="element-558">
            Ticket Promedio
          </p>
          <h3 className="text-4xl font-bold text-light" data-id="element-559">
            $
            {paid.length ? Math.round(total / paid.length).toLocaleString('es-UY') : 0}
          </h3>
        </div>
      </div>

      <div className="bg-[#111111] rounded-2xl border border-white/5 overflow-x-auto shadow-xl" data-id="element-560">
        <table className="w-full text-left text-sm min-w-[800px]" data-id="element-561">
          <thead className="bg-dark/50 text-light-muted text-xs uppercase tracking-wider border-b border-white/5" data-id="element-562">
            <tr data-id="element-563">
              <th className="px-6 py-4 font-medium" data-id="element-564">Fecha y Hora</th>
              <th className="px-6 py-4 font-medium" data-id="element-565">Cliente</th>
              <th className="px-6 py-4 font-medium" data-id="element-566">Servicio / Barbero</th>
              <th className="px-6 py-4 font-medium" data-id="element-567">Método</th>
              <th className="px-6 py-4 font-medium" data-id="element-568">Estado</th>
              <th className="px-6 py-4 font-medium text-right" data-id="element-569">Importe</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5" data-id="element-570">
            {paid.sort((a: any, b: any) => (b.date + b.time).localeCompare(a.date + a.time)).map((a: any) => <tr key={a.id} className="hover:bg-white/5 transition-colors" data-id="element-571">
                  <td className="px-6 py-4 text-light-muted" data-id="element-572">
                    {a.date} <span className="text-light" data-id="element-573">{a.time}</span>
                  </td>
                  <td className="px-6 py-4 font-medium text-light" data-id="element-574">
                    {a.clientName}
                  </td>
                  <td className="px-6 py-4" data-id="element-575">
                    <p className="text-light" data-id="element-576">{a.serviceName}</p>
                    <p className="text-[10px] text-light-muted" data-id="element-577">
                      {a.barberName}
                    </p>
                  </td>
                  <td className="px-6 py-4" data-id="element-578">
                    <span className="px-2.5 py-1 bg-blue-500/10 text-blue-400 rounded-lg text-xs border border-blue-500/20" data-id="element-579">
                      Mercado Pago
                    </span>
                  </td>
                  <td className="px-6 py-4" data-id="element-580">
                    <span className="flex items-center gap-1.5 text-green-400 text-xs font-medium" data-id="element-581">
                      <CheckCircle size={14} data-id="element-582" /> Aprobado
                    </span>
                  </td>
                  <td className="px-6 py-4 font-bold text-gold text-right" data-id="element-583">
                    ${a.price.toLocaleString('es-UY')}
                  </td>
                </tr>)}
            {paid.length === 0 && <tr data-id="element-584">
                <td colSpan={6} className="px-6 py-12 text-center text-light-muted" data-id="element-585">
                  No hay pagos registrados.
                </td>
              </tr>}
          </tbody>
        </table>
      </div>
    </div>;
}
function PromotionsList({
  store,
  setOpenModal
}: {
  store: any;
  setOpenModal: any;
}) {
  const {
    promotions
  } = store;
  return <div className="space-y-6" data-id="element-586">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-587">
        <h2 className="text-lg font-semibold text-light" data-id="element-588">
          Gestión de Promociones
        </h2>
        <button onClick={() => setOpenModal('promo')} className="w-full sm:w-auto bg-gold text-dark px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-gold-hover transition-colors flex justify-center items-center gap-2 shadow-lg shadow-gold/20" data-id="element-589">
          <Plus size={16} data-id="element-590" /> Crear Promoción
        </button>
      </div>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6" data-id="element-591">
        {promotions.map((p: any) => <div key={p.id} className="bg-[#111111] rounded-2xl border border-white/5 p-6 shadow-xl relative group" data-id="element-592">
            <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-593">
              <button className="p-2 text-light-muted hover:text-gold bg-dark rounded-lg border border-white/10" data-id="element-594">
                <Edit2 size={14} data-id="element-595" />
              </button>
              <button className="p-2 text-light-muted hover:text-red-400 bg-dark rounded-lg border border-white/10" data-id="element-596">
                <Trash2 size={14} data-id="element-597" />
              </button>
            </div>
            <div className="w-12 h-12 rounded-xl mb-4 flex items-center justify-center shadow-lg" style={{
          backgroundColor: p.color
        }} data-id="element-598">
              <Tag size={20} className="text-white" data-id="element-599" />
            </div>
            <h3 className="text-xl font-bold text-light mb-1" data-id="element-600">{p.title}</h3>
            <p className="text-sm text-light-muted mb-4" data-id="element-601">{p.subtitle}</p>
            <div className="flex justify-between items-center pt-4 border-t border-white/5" data-id="element-602">
              <span className="text-xs font-medium bg-white/5 px-2.5 py-1 rounded-md text-light-muted border border-white/10 uppercase tracking-wider" data-id="element-603">
                {p.type || 'Descuento'}
              </span>
              <span className={`text-xs font-semibold ${p.active !== false ? 'text-green-400' : 'text-red-400'}`} data-id="element-604">
                {p.active !== false ? 'Activa' : 'Inactiva'}
              </span>
            </div>
          </div>)}
      </div>
    </div>;
}
function Notifications({
  store
}: {
  store: any;
}) {
  return <div className="max-w-4xl space-y-6" data-id="element-605">
      <h2 className="text-lg font-semibold text-light" data-id="element-606">
        Centro de Notificaciones
      </h2>
      <div className="bg-[#111111] rounded-2xl border border-white/5 p-8 shadow-xl space-y-6" data-id="element-607">
        <div data-id="element-608">
          <label className="block text-sm font-medium text-light mb-2" data-id="element-609">
            Segmento de Clientes
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-id="element-610">
            {['Todos', 'Nuevos', 'Frecuentes', 'VIP'].map(s => <button key={s} className="py-3 border border-white/10 rounded-xl text-sm text-light-muted hover:border-gold hover:text-gold transition-colors bg-dark" data-id="element-611">
                {s}
              </button>)}
          </div>
        </div>
        <div data-id="element-612">
          <label className="block text-sm font-medium text-light mb-2" data-id="element-613">
            Tipo de Mensaje
          </label>
          <select className="w-full bg-dark border border-white/10 rounded-xl px-4 py-3 text-sm text-light focus:outline-none focus:border-gold" data-id="element-614">
            <option data-id="element-615">Promoción Especial</option>
            <option data-id="element-616">Recordatorio de Turno</option>
            <option data-id="element-617">Novedades del Local</option>
          </select>
        </div>
        <div data-id="element-618">
          <label className="block text-sm font-medium text-light mb-2" data-id="element-619">
            Mensaje
          </label>
          <textarea rows={5} className="w-full bg-dark border border-white/10 rounded-xl px-4 py-3 text-sm text-light focus:outline-none focus:border-gold resize-none" placeholder="Escribe el mensaje que recibirán los clientes por WhatsApp o Email..." data-id="element-620"></textarea>
        </div>
        <div className="flex justify-end pt-4" data-id="element-621">
          <button className="bg-gold text-dark px-8 py-3 rounded-xl text-sm font-bold hover:bg-gold-hover transition-colors flex items-center gap-2 shadow-lg shadow-gold/20" data-id="element-622">
            <Send size={18} data-id="element-623" /> Enviar Campaña
          </button>
        </div>
      </div>
    </div>;
}
function Stats({
  store
}: {
  store: any;
}) {
  const {
    appointments
  } = store;
  const validAppts = appointments.filter((a: any) => a.status !== 'cancelled');
  const serviceCounts = validAppts.reduce((acc: any, a: any) => {
    acc[a.serviceName] = (acc[a.serviceName] || 0) + 1;
    return acc;
  }, {});
  const serviceData = Object.entries(serviceCounts).map(([name, value]) => ({
    name,
    value
  }));
  const COLORS = ['#C8A548', '#8A7332', '#4D401C', '#E6C673', '#F5DCA8'];
  return <div className="space-y-6" data-id="element-624">
      <h2 className="text-lg font-semibold text-light" data-id="element-625">Reportes Avanzados</h2>
      <div className="grid md:grid-cols-2 gap-6" data-id="element-626">
        <div className="bg-[#111111] rounded-2xl border border-white/5 p-6 shadow-xl h-96 flex flex-col" data-id="element-627">
          <h3 className="text-base font-semibold text-light mb-6 text-center" data-id="element-628">
            Distribución de Servicios
          </h3>
          <ResponsiveContainer width="100%" height="100%" data-id="element-629">
            <PieChart data-id="element-630">
              <Pie data={serviceData} cx="50%" cy="50%" innerRadius={70} outerRadius={100} paddingAngle={5} dataKey="value" stroke="none" data-id="element-631">
                {serviceData.map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} data-id="element-632" />)}
              </Pie>
              <Tooltip contentStyle={{
              backgroundColor: '#1A1A1A',
              borderColor: '#333',
              borderRadius: '8px'
            }} itemStyle={{
              color: '#F5F5F5'
            }} data-id="element-633" />
              <Legend verticalAlign="bottom" height={36} wrapperStyle={{
              fontSize: '12px',
              color: '#A0A0A0'
            }} data-id="element-634" />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-[#111111] rounded-2xl border border-white/5 p-6 shadow-xl flex flex-col justify-center items-center text-center space-y-4" data-id="element-635">
          <AlertCircle size={48} className="text-gold/50" data-id="element-636" />
          <h3 className="text-lg font-medium text-light" data-id="element-637">
            Más reportes en desarrollo
          </h3>
          <p className="text-sm text-light-muted max-w-xs" data-id="element-638">
            Próximamente métricas de ocupación, retención de clientes y
            facturación anual detallada.
          </p>
        </div>
      </div>
    </div>;
}
function SettingsForm({
  store
}: {
  store: any;
}) {
  const {
    config
  } = store;
  const [form, setForm] = useState<ShopConfig>(config);
  const set = (patch: Partial<ShopConfig>) => setForm(f => ({
    ...f,
    ...patch
  }));
  const save = (e: React.FormEvent) => {
    e.preventDefault();
    updateConfig(form);
    toast.success('Configuración guardada exitosamente');
  };
  const field = 'w-full px-4 py-3 bg-dark border border-white/10 rounded-xl text-sm text-light focus:border-gold focus:outline-none transition-colors';
  return <form onSubmit={save} className="max-w-4xl space-y-8 pb-10" data-id="element-639">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-640">
        <h2 className="text-lg font-semibold text-light" data-id="element-641">
          Configuración General
        </h2>
        <button type="submit" className="w-full sm:w-auto px-6 py-2.5 bg-gold text-dark rounded-xl font-bold hover:bg-gold-hover transition-colors text-sm shadow-lg shadow-gold/20" data-id="element-642">
          Guardar Cambios
        </button>
      </div>

      <section className="bg-[#111111] rounded-2xl border border-white/5 p-8 shadow-xl space-y-6" data-id="element-643">
        <h3 className="text-base font-semibold text-gold border-b border-white/5 pb-4" data-id="element-644">
          Identidad de Marca
        </h3>
        <div className="grid sm:grid-cols-2 gap-6" data-id="element-645">
          <Labeled label="Nombre del Local" data-id="element-646">
            <input className={field} value={form.shopName} onChange={e => set({
            shopName: e.target.value
          })} data-id="element-647" />
          </Labeled>
          <Labeled label="Colores de Marca (Hex)" data-id="element-648">
            <input className={field} value={form.brandColors || '#C8A548, #0F0F0F'} onChange={e => set({
            brandColors: e.target.value
          })} placeholder="#C8A548, #0F0F0F" data-id="element-649" />
          </Labeled>
        </div>
      </section>

      <section className="bg-[#111111] rounded-2xl border border-white/5 p-8 shadow-xl space-y-6" data-id="element-650">
        <h3 className="text-base font-semibold text-gold border-b border-white/5 pb-4" data-id="element-651">
          Contacto y Ubicación
        </h3>
        <div className="grid sm:grid-cols-2 gap-6" data-id="element-652">
          <Labeled label="Teléfono" data-id="element-653">
            <input className={field} value={form.phone} onChange={e => set({
            phone: e.target.value
          })} data-id="element-654" />
          </Labeled>
          <Labeled label="Email" data-id="element-655">
            <input className={field} value={form.email} onChange={e => set({
            email: e.target.value
          })} data-id="element-656" />
          </Labeled>
          <Labeled label="Dirección" full data-id="element-657">
            <input className={field} value={form.address} onChange={e => set({
            address: e.target.value
          })} data-id="element-658" />
          </Labeled>
        </div>
      </section>

      <section className="bg-[#111111] rounded-2xl border border-white/5 p-8 shadow-xl space-y-6" data-id="element-659">
        <h3 className="text-base font-semibold text-gold border-b border-white/5 pb-4" data-id="element-660">
          Redes Sociales
        </h3>
        <div className="grid sm:grid-cols-2 gap-6" data-id="element-661">
          <Labeled label="Instagram" data-id="element-662">
            <input className={field} value={form.instagram} onChange={e => set({
            instagram: e.target.value
          })} data-id="element-663" />
          </Labeled>
          <Labeled label="WhatsApp" data-id="element-664">
            <input className={field} value={form.whatsapp} onChange={e => set({
            whatsapp: e.target.value
          })} data-id="element-665" />
          </Labeled>
          <Labeled label="TikTok" data-id="element-666">
            <input className={field} value={form.tiktok} onChange={e => set({
            tiktok: e.target.value
          })} data-id="element-667" />
          </Labeled>
          <Labeled label="Facebook" data-id="element-668">
            <input className={field} value={form.facebook} onChange={e => set({
            facebook: e.target.value
          })} data-id="element-669" />
          </Labeled>
        </div>
      </section>

      <section className="bg-[#111111] rounded-2xl border border-white/5 p-8 shadow-xl space-y-6" data-id="element-670">
        <h3 className="text-base font-semibold text-gold border-b border-white/5 pb-4" data-id="element-671">
          Operativa y Pagos
        </h3>
        <div className="grid sm:grid-cols-3 gap-6" data-id="element-672">
          <Labeled label="Apertura (hs)" data-id="element-673">
            <input type="number" className={field} value={form.openHour} onChange={e => set({
            openHour: Number(e.target.value)
          })} data-id="element-674" />
          </Labeled>
          <Labeled label="Cierre (hs)" data-id="element-675">
            <input type="number" className={field} value={form.closeHour} onChange={e => set({
            closeHour: Number(e.target.value)
          })} data-id="element-676" />
          </Labeled>
          <Labeled label="Duración Turno (min)" data-id="element-677">
            <input type="number" step={15} className={field} value={form.slotMinutes} onChange={e => set({
            slotMinutes: Number(e.target.value)
          })} data-id="element-678" />
          </Labeled>
          <Labeled label="Alias Mercado Pago" full data-id="element-679">
            <input className={field} value={form.mercadoPagoAlias} onChange={e => set({
            mercadoPagoAlias: e.target.value
          })} data-id="element-680" />
          </Labeled>
        </div>
      </section>
    </form>;
}
function AdminProfile() {
  return <div className="max-w-2xl mx-auto space-y-8" data-id="element-681">
      <div className="bg-[#111111] rounded-3xl border border-white/5 p-8 shadow-2xl text-center relative overflow-hidden" data-id="element-682">
        <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-br from-gold/20 to-transparent" data-id="element-683"></div>
        <div className="relative z-10" data-id="element-684">
          <div className="w-32 h-32 mx-auto bg-gradient-to-br from-gold to-yellow-600 rounded-full flex items-center justify-center text-dark font-bold text-5xl shadow-2xl border-4 border-[#111111] mb-6" data-id="element-685">
            A
          </div>
          <h2 className="text-3xl font-bold text-light mb-2" data-id="element-686">
            Admin Principal
          </h2>
          <p className="text-gold font-medium mb-6" data-id="element-687">Propietario / Gerente</p>
          <p className="text-sm text-light-muted max-w-md mx-auto mb-8" data-id="element-688">
            Acceso total al sistema. Gestión de sucursales, finanzas, equipo y
            configuración global de la plataforma BarberOS.
          </p>
          <div className="flex justify-center gap-4" data-id="element-689">
            <button className="bg-dark border border-white/10 text-light px-6 py-3 rounded-xl text-sm font-medium hover:bg-white/5 transition-colors" data-id="element-690">
              Editar Perfil
            </button>
            <button className="bg-dark border border-white/10 text-light px-6 py-3 rounded-xl text-sm font-medium hover:bg-white/5 transition-colors" data-id="element-691">
              Cambiar Contraseña
            </button>
          </div>
        </div>
      </div>
    </div>;
}
function Labeled({
  label,
  full,
  children
}: {
  label: string;
  full?: boolean;
  children: React.ReactNode;
}) {
  return <label className={`block ${full ? 'sm:col-span-2 md:col-span-3' : ''}`} data-id="element-692">
      <span className="block text-xs font-semibold text-light-muted uppercase tracking-wider mb-2" data-id="element-693">
        {label}
      </span>
      {children}
    </label>;
}
function Send(props: any) {
  return <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props} data-id="element-694">
      <line x1="22" y1="2" x2="11" y2="13" data-id="element-695" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" data-id="element-696" />
    </svg>;
}
// --- MODALS ---
function ClientModal({
  store,
  onClose
}: {
  store: any;
  onClose: () => void;
}) {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'Nuevo' | 'Frecuente' | 'VIP'>('Nuevo');
  const [notes, setNotes] = useState('');
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    store.addClient({
      id: `c_${Date.now()}`,
      name,
      phone,
      email,
      status,
      notes,
      createdAt: new Date().toISOString()
    });
    toast.success('Cliente agregado exitosamente');
    onClose();
  };
  return <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4" data-id="element-697">
      <div className="bg-[#111111] border border-white/10 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" data-id="element-698">
        <div className="p-5 border-b border-white/5 flex justify-between items-center shrink-0" data-id="element-699">
          <h2 className="text-lg font-bold text-light" data-id="element-700">Nuevo Cliente</h2>
          <button onClick={onClose} className="text-light-muted hover:text-light" data-id="element-701">
            <X size={20} data-id="element-702" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4 overflow-y-auto flex-1 scrollbar-hide" data-id="element-703">
          <div data-id="element-704">
            <label className="block text-xs font-medium text-light-muted mb-1.5" data-id="element-705">
              Nombre completo
            </label>
            <input required value={name} onChange={e => setName(e.target.value)} className="w-full bg-dark border border-white/10 rounded-xl px-4 py-2.5 text-sm text-light focus:outline-none focus:border-gold" data-id="element-706" />
          </div>
          <div data-id="element-707">
            <label className="block text-xs font-medium text-light-muted mb-1.5" data-id="element-708">
              Teléfono
            </label>
            <input required type="tel" value={phone} onChange={e => setPhone(e.target.value)} className="w-full bg-dark border border-white/10 rounded-xl px-4 py-2.5 text-sm text-light focus:outline-none focus:border-gold" data-id="element-709" />
          </div>
          <div data-id="element-710">
            <label className="block text-xs font-medium text-light-muted mb-1.5" data-id="element-711">
              Email
            </label>
            <input required type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-dark border border-white/10 rounded-xl px-4 py-2.5 text-sm text-light focus:outline-none focus:border-gold" data-id="element-712" />
          </div>
          <div data-id="element-713">
            <label className="block text-xs font-medium text-light-muted mb-1.5" data-id="element-714">
              Estado
            </label>
            <select value={status} onChange={e => setStatus(e.target.value as any)} className="w-full bg-dark border border-white/10 rounded-xl px-4 py-2.5 text-sm text-light focus:outline-none focus:border-gold" data-id="element-715">
              <option value="Nuevo" data-id="element-716">Nuevo</option>
              <option value="Frecuente" data-id="element-717">Frecuente</option>
              <option value="VIP" data-id="element-718">VIP</option>
            </select>
          </div>
          <div data-id="element-719">
            <label className="block text-xs font-medium text-light-muted mb-1.5" data-id="element-720">
              Notas
            </label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} className="w-full bg-dark border border-white/10 rounded-xl px-4 py-2.5 text-sm text-light focus:outline-none focus:border-gold resize-none" data-id="element-721" />
          </div>
          <button type="submit" className="w-full bg-gold text-dark py-3 rounded-xl font-bold hover:bg-gold-hover transition-colors mt-2" data-id="element-722">
            Guardar Cliente
          </button>
        </form>
      </div>
    </div>;
}
function ServiceModal({
  store,
  onClose
}: {
  store: any;
  onClose: () => void;
}) {
  const [name, setName] = useState('');
  const [category, setCategory] = useState('Corte');
  const [duration, setDuration] = useState('30 min');
  const [price, setPrice] = useState('');
  const [desc, setDesc] = useState('');
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    store.addService({
      id: `s_${Date.now()}`,
      name,
      category,
      duration,
      price: Number(price),
      desc,
      active: true
    });
    toast.success('Servicio creado exitosamente');
    onClose();
  };
  return <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4" data-id="element-723">
      <div className="bg-[#111111] border border-white/10 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" data-id="element-724">
        <div className="p-5 border-b border-white/5 flex justify-between items-center shrink-0" data-id="element-725">
          <h2 className="text-lg font-bold text-light" data-id="element-726">Nuevo Servicio</h2>
          <button onClick={onClose} className="text-light-muted hover:text-light" data-id="element-727">
            <X size={20} data-id="element-728" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4 overflow-y-auto flex-1 scrollbar-hide" data-id="element-729">
          <div data-id="element-730">
            <label className="block text-xs font-medium text-light-muted mb-1.5" data-id="element-731">
              Nombre del servicio
            </label>
            <input required value={name} onChange={e => setName(e.target.value)} className="w-full bg-dark border border-white/10 rounded-xl px-4 py-2.5 text-sm text-light focus:outline-none focus:border-gold" data-id="element-732" />
          </div>
          <div className="grid grid-cols-2 gap-4" data-id="element-733">
            <div data-id="element-734">
              <label className="block text-xs font-medium text-light-muted mb-1.5" data-id="element-735">
                Categoría
              </label>
              <input required value={category} onChange={e => setCategory(e.target.value)} className="w-full bg-dark border border-white/10 rounded-xl px-4 py-2.5 text-sm text-light focus:outline-none focus:border-gold" data-id="element-736" />
            </div>
            <div data-id="element-737">
              <label className="block text-xs font-medium text-light-muted mb-1.5" data-id="element-738">
                Duración
              </label>
              <input required value={duration} onChange={e => setDuration(e.target.value)} placeholder="Ej: 30 min" className="w-full bg-dark border border-white/10 rounded-xl px-4 py-2.5 text-sm text-light focus:outline-none focus:border-gold" data-id="element-739" />
            </div>
          </div>
          <div data-id="element-740">
            <label className="block text-xs font-medium text-light-muted mb-1.5" data-id="element-741">
              Precio ($)
            </label>
            <input required type="number" min="0" value={price} onChange={e => setPrice(e.target.value)} className="w-full bg-dark border border-white/10 rounded-xl px-4 py-2.5 text-sm text-light focus:outline-none focus:border-gold" data-id="element-742" />
          </div>
          <div data-id="element-743">
            <label className="block text-xs font-medium text-light-muted mb-1.5" data-id="element-744">
              Descripción
            </label>
            <textarea required value={desc} onChange={e => setDesc(e.target.value)} rows={3} className="w-full bg-dark border border-white/10 rounded-xl px-4 py-2.5 text-sm text-light focus:outline-none focus:border-gold resize-none" data-id="element-745" />
          </div>
          <button type="submit" className="w-full bg-gold text-dark py-3 rounded-xl font-bold hover:bg-gold-hover transition-colors mt-2" data-id="element-746">
            Guardar Servicio
          </button>
        </form>
      </div>
    </div>;
}
function PaymentModal({
  store,
  onClose
}: {
  store: any;
  onClose: () => void;
}) {
  const {
    barbers,
    services
  } = store;
  const [clientName, setClientName] = useState('');
  const [barberId, setBarberId] = useState(barbers[0]?.id || '');
  const [serviceId, setServiceId] = useState(services[0]?.id || '');
  const [paymentMethod, setPaymentMethod] = useState('Efectivo');
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const barber = barbers.find((b: any) => b.id === barberId);
    const service = services.find((s: any) => s.id === serviceId);
    if (!barber || !service) return;
    store.addAppointment({
      id: `a_${Date.now()}`,
      barberId: barber.id,
      barberName: barber.name,
      barberImage: barber.image,
      serviceId: service.id,
      serviceName: service.name,
      price: service.price,
      date: new Date().toISOString().slice(0, 10),
      time: new Date().toTimeString().slice(0, 5),
      clientName,
      clientContact: '',
      paymentStatus: 'approved',
      status: 'completed',
      createdAt: new Date().toISOString(),
      paymentMethod
    });
    toast.success('Pago registrado exitosamente');
    onClose();
  };
  return <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4" data-id="element-747">
      <div className="bg-[#111111] border border-white/10 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" data-id="element-748">
        <div className="p-5 border-b border-white/5 flex justify-between items-center shrink-0" data-id="element-749">
          <h2 className="text-lg font-bold text-light" data-id="element-750">Registrar Pago</h2>
          <button onClick={onClose} className="text-light-muted hover:text-light" data-id="element-751">
            <X size={20} data-id="element-752" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4 overflow-y-auto flex-1 scrollbar-hide" data-id="element-753">
          <div data-id="element-754">
            <label className="block text-xs font-medium text-light-muted mb-1.5" data-id="element-755">
              Nombre del Cliente
            </label>
            <input required value={clientName} onChange={e => setClientName(e.target.value)} className="w-full bg-dark border border-white/10 rounded-xl px-4 py-2.5 text-sm text-light focus:outline-none focus:border-gold" data-id="element-756" />
          </div>
          <div data-id="element-757">
            <label className="block text-xs font-medium text-light-muted mb-1.5" data-id="element-758">
              Barbero
            </label>
            <select required value={barberId} onChange={e => setBarberId(e.target.value)} className="w-full bg-dark border border-white/10 rounded-xl px-4 py-2.5 text-sm text-light focus:outline-none focus:border-gold" data-id="element-759">
              {barbers.map((b: any) => <option key={b.id} value={b.id} data-id="element-760">
                  {b.name}
                </option>)}
            </select>
          </div>
          <div data-id="element-761">
            <label className="block text-xs font-medium text-light-muted mb-1.5" data-id="element-762">
              Servicio
            </label>
            <select required value={serviceId} onChange={e => setServiceId(e.target.value)} className="w-full bg-dark border border-white/10 rounded-xl px-4 py-2.5 text-sm text-light focus:outline-none focus:border-gold" data-id="element-763">
              {services.map((s: any) => <option key={s.id} value={s.id} data-id="element-764">
                  {s.name} (${s.price})
                </option>)}
            </select>
          </div>
          <div data-id="element-765">
            <label className="block text-xs font-medium text-light-muted mb-1.5" data-id="element-766">
              Método de Pago
            </label>
            <select value={paymentMethod} onChange={e => setPaymentMethod(e.target.value)} className="w-full bg-dark border border-white/10 rounded-xl px-4 py-2.5 text-sm text-light focus:outline-none focus:border-gold" data-id="element-767">
              <option value="Efectivo" data-id="element-768">Efectivo</option>
              <option value="Mercado Pago" data-id="element-769">Mercado Pago</option>
              <option value="Tarjeta" data-id="element-770">Tarjeta</option>
            </select>
          </div>
          <button type="submit" className="w-full bg-gold text-dark py-3 rounded-xl font-bold hover:bg-gold-hover transition-colors mt-2" data-id="element-771">
            Confirmar Pago
          </button>
        </form>
      </div>
    </div>;
}
function PromoModal({
  store,
  onClose
}: {
  store: any;
  onClose: () => void;
}) {
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [discount, setDiscount] = useState('');
  const [color, setColor] = useState('#C8A548');
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    store.addPromotion({
      id: `p_${Date.now()}`,
      title,
      subtitle,
      discount,
      color,
      active: true
    });
    toast.success('Promoción creada exitosamente');
    onClose();
  };
  return <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4" data-id="element-772">
      <div className="bg-[#111111] border border-white/10 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden flex flex-col max-h-[90vh]" data-id="element-773">
        <div className="p-5 border-b border-white/5 flex justify-between items-center shrink-0" data-id="element-774">
          <h2 className="text-lg font-bold text-light" data-id="element-775">Crear Promoción</h2>
          <button onClick={onClose} className="text-light-muted hover:text-light" data-id="element-776">
            <X size={20} data-id="element-777" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4 overflow-y-auto flex-1 scrollbar-hide" data-id="element-778">
          <div data-id="element-779">
            <label className="block text-xs font-medium text-light-muted mb-1.5" data-id="element-780">
              Título
            </label>
            <input required value={title} onChange={e => setTitle(e.target.value)} placeholder="Ej: 20% OFF" className="w-full bg-dark border border-white/10 rounded-xl px-4 py-2.5 text-sm text-light focus:outline-none focus:border-gold" data-id="element-781" />
          </div>
          <div data-id="element-782">
            <label className="block text-xs font-medium text-light-muted mb-1.5" data-id="element-783">
              Subtítulo
            </label>
            <input required value={subtitle} onChange={e => setSubtitle(e.target.value)} placeholder="Ej: Lunes y martes" className="w-full bg-dark border border-white/10 rounded-xl px-4 py-2.5 text-sm text-light focus:outline-none focus:border-gold" data-id="element-784" />
          </div>
          <div className="grid grid-cols-2 gap-4" data-id="element-785">
            <div data-id="element-786">
              <label className="block text-xs font-medium text-light-muted mb-1.5" data-id="element-787">
                Descuento
              </label>
              <input required value={discount} onChange={e => setDiscount(e.target.value)} placeholder="Ej: 20%" className="w-full bg-dark border border-white/10 rounded-xl px-4 py-2.5 text-sm text-light focus:outline-none focus:border-gold" data-id="element-788" />
            </div>
            <div data-id="element-789">
              <label className="block text-xs font-medium text-light-muted mb-1.5" data-id="element-790">
                Color de Tarjeta
              </label>
              <input required type="color" value={color} onChange={e => setColor(e.target.value)} className="w-full h-[42px] bg-dark border border-white/10 rounded-xl cursor-pointer" data-id="element-791" />
            </div>
          </div>
          <button type="submit" className="w-full bg-gold text-dark py-3 rounded-xl font-bold hover:bg-gold-hover transition-colors mt-2" data-id="element-792">
            Guardar Promoción
          </button>
        </form>
      </div>
    </div>;
}